import { useState, useMemo } from 'react';
import {
    FormControl,
    Stack,
    FormLabel,
    HStack,
    useDisclosure,
} from '@chakra-ui/react';
import { Button, Text } from 'components/v4';
import { Field, useFormikContext } from 'formik';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import { usePartnerForm } from '../../hooks/usePartnerForm';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import { formLabelStyling } from 'components/forms/new/formStyles';
import { FORM_FIELD_PATHS } from '../../constants';
import { stackSpacing, stackStyling } from 'components/forms/new/formStyles';
import ChangeBillingDay from '../../../../companies/edit/ChangeBillingDay';

const BillTo = ({ darkMode, errors }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { formData, updateFormData } = usePartnerForm();
    const { setFieldValue } = useFormikContext();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const hasError = !!errors?.billTo;

    const billingDayDropdown = useMemo(
        () =>
            Array.from({ length: 28 }, (_, i) => ({
                value: (i + 1).toString(),
                label: (i + 1).toString(),
                id: (i + 1).toString(),
            })),
        [],
    );

    return (
        <>
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={hasError}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)} mb={0}>
                        Bill To
                    </FormLabel>
                    <HStack
                        flex={1}
                        maxWidth="400px"
                        display="flex"
                        alignItems="center">
                        <Field
                            component={DropDown}
                            id={FORM_FIELD_PATHS.BILL_TO}
                            name={FORM_FIELD_PATHS.BILL_TO}
                            noPadding
                            data={['Partner', 'Wholesaler'].map((val) => ({ 
                                id: val, 
                                name: val 
                            }))}
                            currentId={formData?.partnerBillingSettings?.billTo}
                            selectedId="id"
                            shownValue="name"
                            placeholder="Bill To..."
                            handleSelectChange={(item) => {
                                updateFormData(FORM_FIELD_PATHS.BILL_TO, item?.id);
                                setFieldValue(FORM_FIELD_PATHS.BILL_TO, item?.id);
                            }}
                            width="100%"
                            isLoading={false}
                            darkMode={darkMode}
                        />
                        <>
                            <Text fontWeight={500}>{'Billing Day: '}</Text>
                            {formData?.partnerBillingSettings?.billingDay || '0'}
                        </>
                        <Button size="sm" height="30px" onClick={onOpen}>
                            Update
                        </Button>
                    </HStack>
                    {isHovered && (
                        <FieldToolTip
                            label="Opens up a modal to change the billing day for this entity"
                            ariaLabel="billto-tip"
                            left="25px"
                            top="25px"
                        />
                    )}
                </Stack>
            </FormControl>
            <ChangeBillingDay
                openModal={isOpen}
                handleCloseModal={onClose}
                mode="Partner"
                billingDayDropdown={billingDayDropdown}
                currentBillingDay={formData?.partnerBillingSettings?.billingDay}
                partnerID={formData?.id}
                handleRefreshModal={(data, directValue) => {
                    // If API succeeds, extract from response data
                    // If API fails (no invoice), use directValue passed from modal
                    const newBillingDay = directValue || data?.partnerBillingSettings?.billingDay;
                    updateFormData(FORM_FIELD_PATHS.BILLING_DAY, newBillingDay);
                    setFieldValue(FORM_FIELD_PATHS.BILLING_DAY, newBillingDay);
                }}
            />
        </>
    );
};

export default BillTo;
